import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  Check,
  ArrowRight,
  ArrowLeft,
  Navigation,
  Plus,
  X,
  Sparkles,
  Zap,
  Award,
  Clock,
  ShieldCheck,
  Flame,
  CheckCircle2,
} from 'lucide-react';
import { SportIcon } from '@/components/SportIcon';
import DynamicPlayerCard, { AVATAR_OPTIONS, SKILL_TIER_LABELS } from '@/components/DynamicPlayerCard';
import InteractiveRadarScanner from '@/components/InteractiveRadarScanner';
import { createClient } from '@/lib/supabase/client';
import { updateUserProfileApi, recordConsentApi } from '@/lib/api';

export interface DynamicOnboardingWizardProps {
  isModal?: boolean;
  onClose?: () => void;
}

interface SportOption {
  id: string;
  name: string;
  sport: string;
  desc: string;
  roles: string[];
  isCustom?: boolean;
}

const DEFAULT_SPORTS: SportOption[] = [
  {
    id: 'cricket',
    name: 'Cricket',
    sport: 'CRICKET',
    desc: 'Box cricket & turf matches',
    roles: ['Top Order Batsman', 'Pace Bowler', 'Spin Wizard', 'All-Rounder', 'Wicketkeeper'],
  },
  {
    id: 'football',
    name: 'Football',
    sport: 'FOOTBALL',
    desc: '5v5, 7v7 turf games',
    roles: ['Striker / Finisher', 'Playmaker Midfield', 'Box-to-Box', 'Lockdown Defender', 'Goalkeeper'],
  },
  {
    id: 'badminton',
    name: 'Badminton',
    sport: 'BADMINTON',
    desc: 'Singles & doubles rallies',
    roles: ['Singles Specialist', 'Front Court Attacker', 'Power Smasher', 'Doubles Tactician'],
  },
  {
    id: 'basketball',
    name: 'Basketball',
    sport: 'BASKETBALL',
    desc: 'Full & half-court pickup',
    roles: ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  },
  {
    id: 'tennis',
    name: 'Tennis',
    sport: 'TENNIS',
    desc: 'Hard & clay court sets',
    roles: ['Baseline Grinder', 'Aggressive Baseliner', 'Serve & Volley', 'All-Court'],
  },
  {
    id: 'pickleball',
    name: 'Pickleball',
    sport: 'PICKLEBALL',
    desc: 'Casual dinking & open games',
    roles: ['Kitchen Dinker', 'Drive & Drop Specialist', 'Erne Master', 'All-Court'],
  },
  {
    id: 'volleyball',
    name: 'Volleyball',
    sport: 'VOLLEYBALL',
    desc: 'Sand & indoor court sets',
    roles: ['Outside Spiker', 'Middle Blocker', 'Setter', 'Libero Defense'],
  },
];

const SKILL_LEVELS = [
  {
    id: 'beginner',
    title: 'Beginner / First Timer',
    badge: 'ROOKIE',
    desc: 'Learning the rules, looking for fun and friendly rallies with zero pressure.',
  },
  {
    id: 'casual',
    title: 'Casual / Weekend Player',
    badge: 'BALLER',
    desc: 'Play once or twice a week for cardio, fun, and meeting sports friends.',
  },
  {
    id: 'intermediate',
    title: 'Intermediate / Regular',
    badge: 'TACTICAL',
    desc: 'Good game fundamentals, consistent attendance, competitive spirit.',
  },
  {
    id: 'advanced',
    title: 'Advanced / Competitive',
    badge: 'ELITE',
    desc: 'Tournament experience, fast-paced matches, high endurance.',
  },
];

const MATCH_WINDOWS = [
  { id: 'morning', label: 'Morning Glory (6 AM - 9 AM)', icon: '🌅', desc: 'Fresh air & sunrise cardio' },
  { id: 'evening', label: 'Sunset Pickups (4 PM - 7 PM)', icon: '🌇', desc: 'After-work & college runs' },
  { id: 'night', label: 'Floodlight Nights (7 PM - 11 PM)', icon: '⚡', desc: 'Prime turf time under lights' },
  { id: 'weekend', label: 'Weekend Tournaments', icon: '🏆', desc: 'Saturday & Sunday showdowns' },
];

function calculateAge(birthDateString: string): number {
  if (!birthDateString) return 0;
  const parts = birthDateString.split('-').map(Number);
  if (parts.length < 3 || isNaN(parts[0])) return 0;
  const birthDate = new Date(parts[0], parts[1] - 1, parts[2]);
  const now = new Date();
  let age = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Lightweight pure canvas confetti burst
function triggerConfettiBurst(canvas: HTMLCanvasElement | null) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const particles: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    color: string;
    rotation: number;
    vRot: number;
    alpha: number;
  }> = [];

  const colors = ['#7c3aed', '#a855f7', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#ffffff'];

  for (let i = 0; i < 75; i++) {
    particles.push({
      x: canvas.width / 2,
      y: canvas.height / 2,
      vx: (Math.random() - 0.5) * 14,
      vy: (Math.random() - 0.7) * 16,
      size: Math.random() * 8 + 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      vRot: (Math.random() - 0.5) * 12,
      alpha: 1,
    });
  }

  let frame = 0;
  function animate() {
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.35; // gravity
      p.rotation += p.vRot;
      p.alpha -= 0.012;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.7);
      ctx.restore();
    });

    frame++;
    if (frame < 90) {
      requestAnimationFrame(animate);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
  requestAnimationFrame(animate);
}

export default function DynamicOnboardingWizard({ isModal = false, onClose }: DynamicOnboardingWizardProps) {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [step, setStep] = useState<number>(1);
  const totalSteps = 4;

  // Step 1: Athlete Identity & Avatar
  const [handle, setHandle] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      try {
        const uStr = localStorage.getItem('kheldrop_auth_user');
        if (uStr) {
          const u = JSON.parse(uStr);
          return u.username || u.handle || (u.email ? u.email.split('@')[0] : 'athlete');
        }
      } catch {}
    }
    return 'athlete';
  });

  const [name, setName] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      try {
        const uStr = localStorage.getItem('kheldrop_auth_user');
        if (uStr) {
          const u = JSON.parse(uStr);
          return u.user_metadata?.full_name || u.name || '';
        }
      } catch {}
    }
    return '';
  });

  const [avatarId, setAvatarId] = useState<string>('striker');

  // Step 2: Sport DNA & Tactical Roles
  const [selectedSports, setSelectedSports] = useState<string[]>(['cricket', 'football']);
  const [customSports, setCustomSports] = useState<SportOption[]>([]);
  const [tacticalRoles, setTacticalRoles] = useState<Record<string, string>>({
    cricket: 'Top Order Batsman',
    football: 'Striker / Finisher',
  });
  const [showOtherInput, setShowOtherInput] = useState<boolean>(false);
  const [customSportName, setCustomSportName] = useState<string>('');
  const [customSportRole, setCustomSportRole] = useState<string>('');

  // Step 3: Intensity & Schedule
  const [skillLevel, setSkillLevel] = useState<string>('casual');
  const [matchWindow, setMatchWindow] = useState<string>('Evening Floodlights (7 PM - 10 PM)');

  // Step 4: Live Turf Radar & Location + Age Gate
  const [locationStatus, setLocationStatus] = useState<'idle' | 'prompting' | 'granted' | 'denied'>('idle');
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedCityArea, setSelectedCityArea] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const savedDistrict = localStorage.getItem('kheldrop_selected_district');
      if (savedDistrict) {
        try {
          const d = JSON.parse(savedDistrict);
          if (d?.name) return `${d.name}, ${d.state || ''}`.trim();
        } catch {}
      }
    }
    return 'Shahdara, Delhi';
  });
  const [radiusKm, setRadiusKm] = useState<number>(5);
  const [onboardingDob, setOnboardingDob] = useState<string>('');
  const [ageAffirmed, setAgeAffirmed] = useState<boolean>(false);
  const [ageError, setAgeError] = useState<string>('');
  const [needsAgeGate, setNeedsAgeGate] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      try {
        const uStr = localStorage.getItem('kheldrop_auth_user');
        if (uStr) {
          const u = JSON.parse(uStr);
          return !u.dob && !u.age && !u.user_metadata?.dob && !u.user_metadata?.age;
        }
      } catch {}
    }
    return false;
  });

  // Step 5: Certified & Celebrate
  const [isCertified, setIsCertified] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [passId] = useState<string>(() => `KD-${Math.floor(1000 + Math.random() * 9000)}-${new Date().getFullYear()}`);

  const allAvailableSports = [...DEFAULT_SPORTS, ...customSports];

  const toggleSport = (id: string) => {
    setSelectedSports((prev) => {
      const isAlreadySelected = prev.includes(id);
      if (isAlreadySelected) {
        if (prev.length <= 1) return prev; // Keep at least one
        return prev.filter((s) => s !== id);
      } else {
        // Auto-assign default tactical role if available
        const spObj = allAvailableSports.find((s) => s.id === id);
        if (spObj && spObj.roles.length > 0 && !tacticalRoles[id]) {
          setTacticalRoles((r) => ({ ...r, [id]: spObj.roles[0] }));
        }
        return [...prev, id];
      }
    });
  };

  const handleSelectRole = (sportId: string, roleName: string) => {
    setTacticalRoles((prev) => ({
      ...prev,
      [sportId]: roleName,
    }));
  };

  const handleAddCustomSport = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = customSportName.trim();
    if (!trimmed) return;

    const id = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const existing = allAvailableSports.find((s) => s.id === id);

    if (!existing) {
      const newCustom: SportOption = {
        id,
        name: trimmed,
        sport: id.toUpperCase(),
        desc: 'Custom community sport',
        roles: customSportRole.trim() ? [customSportRole.trim()] : ['Player'],
        isCustom: true,
      };
      setCustomSports((prev) => [...prev, newCustom]);
      setSelectedSports((prev) => [...prev, id]);
      if (customSportRole.trim()) {
        setTacticalRoles((r) => ({ ...r, [id]: customSportRole.trim() }));
      }
    } else if (!selectedSports.includes(id)) {
      setSelectedSports((prev) => [...prev, id]);
    }

    setCustomSportName('');
    setCustomSportRole('');
    setShowOtherInput(false);
  };

  const handleRemoveCustomSport = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setCustomSports((prev) => prev.filter((s) => s.id !== id));
    setSelectedSports((prev) => prev.filter((s) => s !== id));
  };

  const handleRequestLiveLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setLocationStatus('prompting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocationCoords(coords);
        setLocationStatus('granted');
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_live_coords', JSON.stringify(coords));
          localStorage.setItem('kheldrop_location_permission', 'granted');
        }
      },
      (err) => {
        console.warn('Location permission error:', err);
        setLocationStatus('denied');
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_location_permission', 'denied');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleFinishOnboarding = async (options?: { startTour?: boolean }) => {
    if (needsAgeGate) {
      if (!onboardingDob) {
        setAgeError('Please select your Date of Birth.');
        setStep(4);
        return;
      }
      const calculatedAge = calculateAge(onboardingDob);
      if (calculatedAge < 18) {
        setAgeError('Strict age limit: You must be at least 18 years old to join KhelDrop under DPDP Act, 2023 §9.');
        setStep(4);
        return;
      }
      if (!ageAffirmed) {
        setAgeError('You must solemnly affirm that you are 18+ and accept the Terms of Service.');
        setStep(4);
        return;
      }
    }

    setSaving(true);
    setIsCertified(true);

    try {
      const allSelectedSports = selectedSports.map((s) => {
        const match = allAvailableSports.find((item) => item.id === s);
        return match ? match.name : s;
      });

      const preferences = {
        sports: allSelectedSports,
        tacticalRoles,
        skillLevel,
        area: selectedCityArea,
        radiusKm,
        matchWindow,
        avatarId,
        handle: handle.startsWith('@') ? handle : `@${handle}`,
        passId,
        coords: locationCoords,
        completedAt: new Date().toISOString(),
      };

      if (typeof window !== 'undefined') {
        localStorage.setItem('kheldrop_player_preferences', JSON.stringify(preferences));
        localStorage.setItem('kheldrop_onboarding_completed', 'true');

        const storedUser = localStorage.getItem('kheldrop_auth_user');
        if (storedUser) {
          try {
            const u = JSON.parse(storedUser);
            if (needsAgeGate) {
              recordConsentApi(
                u.id || 'user',
                'ONBOARDING_AGE_AFFIRMATION_AND_TERMS',
                '2026.1',
                'dynamic_onboarding'
              ).catch(() => {});
            }

            const updatedUser = {
              ...u,
              name: name || u.name,
              handle: handle.startsWith('@') ? handle : `@${handle}`,
              avatarId,
              sports: allSelectedSports,
              tacticalRoles,
              skillLevel,
              maxDistance: radiusKm,
              districtName: selectedCityArea,
              dob: onboardingDob || u.dob,
              age: onboardingDob ? calculateAge(onboardingDob) : u.age,
              is_adult: true,
              currentLocation: locationCoords || u.currentLocation,
              user_metadata: {
                ...(u.user_metadata || {}),
                full_name: name || u.user_metadata?.full_name,
                handle,
                avatarId,
                sports: allSelectedSports,
                tacticalRoles,
                skillLevel,
                preferences,
                onboardingCompleted: true,
              },
            };
            localStorage.setItem('kheldrop_auth_user', JSON.stringify(updatedUser));
            window.dispatchEvent(new CustomEvent('kheldrop_auth_change', { detail: updatedUser }));
          } catch {}
        }
      }

      await updateUserProfileApi({
        name,
        sports: allSelectedSports,
        skillLevel,
        preferences,
        maxDistance: radiusKm,
        currentLocation: locationCoords,
        districtName: selectedCityArea,
      });

      try {
        const supabase = createClient();
        await supabase.auth.updateUser({
          data: {
            full_name: name,
            handle,
            avatarId,
            sports: allSelectedSports,
            tacticalRoles,
            skillLevel,
            preferences,
            onboardingCompleted: true,
          },
        });
      } catch {}

      // Trigger Confetti Celebration!
      triggerConfettiBurst(canvasRef.current);

      setTimeout(() => {
        setSaving(false);
        if (onClose) onClose();

        if (options?.startTour) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
          }
          navigate('/');
        } else {
          navigate('/');
        }
      }, 700);
    } catch (err) {
      console.warn('Error saving dynamic onboarding:', err);
      setSaving(false);
      if (onClose) onClose();
      navigate('/');
    }
  };

  const handleSkip = () => {
    if (step < totalSteps) {
      setStep((prev) => prev + 1);
    } else {
      handleFinishOnboarding();
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: isModal ? '980px' : '1080px',
        margin: '0 auto',
        background: '#ffffff',
        borderRadius: isModal ? '28px' : '24px',
        overflow: 'hidden',
        color: '#09090b',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Confetti Overlay Canvas */}
      <canvas
        ref={canvasRef}
        width={980}
        height={700}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 9999,
          width: '100%',
          height: '100%',
        }}
      />

      {/* Top Bar Header */}
      <div
        style={{
          padding: '18px 28px',
          borderBottom: '1px solid #f0f0f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#ffffff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              background: '#09090b',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <Zap size={18} color="#a855f7" />
          </div>
          <div>
            <div style={{ fontWeight: 900, fontSize: '16px', letterSpacing: '-0.02em', color: '#09090b' }}>
              KhelDrop Dynamic Athlete Setup
            </div>
            <div style={{ fontSize: '11px', color: '#71717a', fontWeight: 600 }}>
              Build your live Player Pass & activate match radar
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 800,
              color: '#6d28d9',
              background: 'rgba(109, 40, 217, 0.08)',
              padding: '5px 12px',
              borderRadius: '999px',
            }}
          >
            Step {step} of {totalSteps}
          </div>

          {isModal && onClose && (
            <button
              onClick={() => handleFinishOnboarding()}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '8px',
                display: 'flex',
              }}
              title="Close and save"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ height: '4px', background: '#f4f4f5', width: '100%' }}>
        <div
          style={{
            height: '100%',
            width: `${(step / totalSteps) * 100}%`,
            background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
            transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      </div>

      {/* Main 2-Column Responsive Body */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(300px, 380px) 1fr',
          gap: '28px',
          padding: '28px',
          alignItems: 'start',
          maxHeight: isModal ? 'calc(88vh - 130px)' : 'none',
          overflowY: 'auto',
        }}
        className="dynamic-onboarding-grid"
      >
        {/* LEFT COLUMN: LIVE DYNAMIC ATHLETE CARD PREVIEW */}
        <div
          style={{
            position: 'sticky',
            top: '0px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 4px',
            }}
          >
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Live Passport Preview
            </span>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }} />
              Updating Realtime
            </span>
          </div>

          <DynamicPlayerCard
            name={name}
            handle={handle}
            avatarId={avatarId}
            sports={selectedSports.map((s) => {
              const obj = allAvailableSports.find((item) => item.id === s);
              return obj ? obj.name : s;
            })}
            tacticalRoles={tacticalRoles}
            skillLevel={skillLevel}
            area={selectedCityArea}
            radiusKm={radiusKm}
            matchWindow={matchWindow}
            isVerifiedAdult={!needsAgeGate || (Boolean(onboardingDob) && calculateAge(onboardingDob) >= 18 && ageAffirmed)}
            passId={passId}
            isCertified={isCertified}
          />

          <div
            style={{
              fontSize: '11.5px',
              color: '#71717a',
              textAlign: 'center',
              padding: '6px 12px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
            }}
          >
            Hover or tilt to inspect your 3D digital player pass
          </div>
        </div>

        {/* RIGHT COLUMN: STEP INTERACTIVE WIZARD */}
        <div>
          {/* STEP 1: ATHLETE IDENTITY & PERSONA */}
          {step === 1 && (
            <div>
              <div style={{ marginBottom: '22px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(124, 58, 237, 0.08)',
                    color: '#7c3aed',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 800,
                    marginBottom: '8px',
                  }}
                >
                  <Sparkles size={13} /> ATHLETE PERSONA
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.02em', color: '#09090b' }}>
                  Create Your Player Identity
                </h2>
                <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                  Choose your avatar persona and handle. This is what hosts and teammates see when you join matches.
                </p>
              </div>

              {/* Avatar Archetype Grid */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, marginBottom: '8px', color: '#1e293b' }}>
                  Choose Your Sports Avatar
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  {AVATAR_OPTIONS.map((av) => {
                    const isSelected = avatarId === av.id;
                    return (
                      <button
                        key={av.id}
                        type="button"
                        onClick={() => setAvatarId(av.id)}
                        style={{
                          background: isSelected ? 'rgba(124, 58, 237, 0.08)' : '#f8fafc',
                          border: isSelected ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                          borderRadius: '16px',
                          padding: '12px 8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          boxShadow: isSelected ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none',
                        }}
                      >
                        <span style={{ fontSize: '26px' }}>{av.emoji}</span>
                        <span style={{ fontSize: '11.5px', fontWeight: 800, color: isSelected ? '#7c3aed' : '#334155' }}>
                          {av.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name & Handle Inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Ashutosh Yadav"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid #cbd5e1',
                      background: '#ffffff',
                      fontSize: '14px',
                      color: '#0f172a',
                      fontWeight: 600,
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>
                    Player Handle (@gamertag)
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="athlete_one"
                      value={handle.replace(/^@/, '')}
                      onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      style={{
                        width: '100%',
                        padding: '11px 14px 11px 28px',
                        borderRadius: '12px',
                        border: '1.5px solid #cbd5e1',
                        background: '#ffffff',
                        fontSize: '14px',
                        color: '#0f172a',
                        fontWeight: 700,
                        outline: 'none',
                      }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#94a3b8',
                        fontWeight: 800,
                        fontSize: '14px',
                      }}
                    >
                      @
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: SPORT DNA & TACTICAL ROLES */}
          {step === 2 && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(124, 58, 237, 0.08)',
                    color: '#7c3aed',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 800,
                    marginBottom: '8px',
                  }}
                >
                  <Award size={13} /> SPORTS DNA & ROLES
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.02em', color: '#09090b' }}>
                  What Sports & Roles Do You Play?
                </h2>
                <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                  Select all sports you play. Tap tactical roles to let team captains know your specialty on the pitch.
                </p>
              </div>

              {/* Sports Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {allAvailableSports.map((sport) => {
                  const isSelected = selectedSports.includes(sport.id);
                  return (
                    <button
                      key={sport.id}
                      type="button"
                      onClick={() => toggleSport(sport.id)}
                      style={{
                        background: isSelected ? '#09090b' : '#ffffff',
                        color: isSelected ? '#ffffff' : '#09090b',
                        border: isSelected ? '1.5px solid #09090b' : '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '14px 12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        position: 'relative',
                        boxShadow: isSelected ? '0 6px 16px rgba(0,0,0,0.15)' : 'none',
                      }}
                    >
                      {sport.isCustom && (
                        <span
                          onClick={(e) => handleRemoveCustomSport(e, sport.id)}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            color: isSelected ? '#d4d4d8' : '#94a3b8',
                            cursor: 'pointer',
                          }}
                        >
                          <X size={14} />
                        </span>
                      )}
                      <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center' }}>
                        <SportIcon sport={sport.sport} size={24} color={isSelected ? '#c084fc' : '#09090b'} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: '13.5px' }}>{sport.name}</div>
                        <div style={{ fontSize: '11px', color: isSelected ? '#d4d4d8' : '#94a3b8', marginTop: '2px' }}>
                          {sport.desc}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Add Custom Sport Button */}
                <button
                  type="button"
                  onClick={() => setShowOtherInput((prev) => !prev)}
                  style={{
                    background: showOtherInput ? '#f8fafc' : '#ffffff',
                    border: '1.5px dashed #7c3aed',
                    borderRadius: '16px',
                    padding: '14px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: 'rgba(124, 58, 237, 0.1)',
                      color: '#7c3aed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Plus size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '13.5px', color: '#7c3aed' }}>+ Other Sport</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Add Squash, Padel, etc.</div>
                  </div>
                </button>
              </div>

              {/* Custom Sport Input Drawer */}
              {showOtherInput && (
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '14px',
                    padding: '12px 14px',
                    marginBottom: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Sport Name (e.g. Squash, Padel, Swimming, Rugby...)"
                      value={customSportName}
                      onChange={(e) => setCustomSportName(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '9px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Preferred Role (e.g. Singles Ace)"
                      value={customSportRole}
                      onChange={(e) => setCustomSportRole(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '9px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomSport}
                      disabled={!customSportName.trim()}
                      style={{
                        background: '#7c3aed',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '9px 16px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: customSportName.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              )}

              {/* Interactive Tactical Role Expanders for Selected Sports */}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, marginBottom: '8px', color: '#1e293b' }}>
                  Pick Your Tactical Roles:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedSports.map((spId) => {
                    const sp = allAvailableSports.find((item) => item.id === spId);
                    if (!sp || sp.roles.length === 0) return null;
                    const activeRole = tacticalRoles[spId] || sp.roles[0];

                    return (
                      <div
                        key={spId}
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '10px 14px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 800, marginBottom: '8px' }}>
                          <SportIcon sport={sp.sport} size={15} color="#7c3aed" />
                          <span>{sp.name} Role:</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {sp.roles.map((r) => {
                            const isRoleActive = activeRole === r;
                            return (
                              <button
                                key={r}
                                type="button"
                                onClick={() => handleSelectRole(spId, r)}
                                style={{
                                  background: isRoleActive ? '#7c3aed' : '#ffffff',
                                  color: isRoleActive ? '#ffffff' : '#334155',
                                  border: isRoleActive ? '1px solid #7c3aed' : '1px solid #cbd5e1',
                                  borderRadius: '999px',
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                {isRoleActive && <Check size={11} style={{ display: 'inline', marginRight: '4px' }} />}
                                {r}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: INTENSITY DIAL & SCHEDULE */}
          {step === 3 && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(124, 58, 237, 0.08)',
                    color: '#7c3aed',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 800,
                    marginBottom: '8px',
                  }}
                >
                  <Flame size={13} /> COMPETITIVENESS & SCHEDULE
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.02em', color: '#09090b' }}>
                  Skill Level & Match Windows
                </h2>
                <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                  This guarantees you are matched into balanced games with players of equal endurance and pace.
                </p>
              </div>

              {/* Skill Levels */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                {SKILL_LEVELS.map((lvl) => {
                  const isSelected = skillLevel === lvl.id;
                  return (
                    <div
                      key={lvl.id}
                      onClick={() => setSkillLevel(lvl.id)}
                      style={{
                        background: isSelected ? 'rgba(124, 58, 237, 0.05)' : '#ffffff',
                        border: isSelected ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 800, fontSize: '14px', color: '#09090b' }}>
                            {lvl.title}
                          </span>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 900,
                              background: isSelected ? '#7c3aed' : '#f1f5f9',
                              color: isSelected ? '#ffffff' : '#64748b',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }}
                          >
                            {lvl.badge}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
                          {lvl.desc}
                        </div>
                      </div>
                      <div
                        style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          border: isSelected ? '5px solid #7c3aed' : '2px solid #cbd5e1',
                          background: '#ffffff',
                          flexShrink: 0,
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Match Timing Windows */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, marginBottom: '8px', color: '#1e293b' }}>
                  Preferred Playing Window
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {MATCH_WINDOWS.map((win) => {
                    const isSelected = matchWindow === win.label;
                    return (
                      <button
                        key={win.id}
                        type="button"
                        onClick={() => setMatchWindow(win.label)}
                        style={{
                          background: isSelected ? '#09090b' : '#f8fafc',
                          color: isSelected ? '#ffffff' : '#09090b',
                          border: isSelected ? '1.5px solid #09090b' : '1px solid #e2e8f0',
                          borderRadius: '14px',
                          padding: '12px 14px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{ fontSize: '18px', marginBottom: '4px' }}>{win.icon}</div>
                        <div style={{ fontWeight: 800, fontSize: '13px' }}>{win.label.split('(')[0]}</div>
                        <div style={{ fontSize: '11px', color: isSelected ? '#cbd5e1' : '#64748b', marginTop: '2px' }}>
                          {win.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: LIVE TURF RADAR & DPDP 18+ GATE */}
          {step === 4 && (
            <div>
              <div style={{ marginBottom: '18px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#059669',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 800,
                    marginBottom: '8px',
                  }}
                >
                  <Navigation size={13} /> RADAR SCANNER & VERIFICATION
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.02em', color: '#09090b' }}>
                  Match Radar & Statutory Verification
                </h2>
                <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                  Test your real-time match radar, configure your sports hub, and affirm compliance under DPDP Act, 2023.
                </p>
              </div>

              {/* Interactive Radar Component */}
              <div style={{ marginBottom: '20px' }}>
                <InteractiveRadarScanner
                  selectedArea={selectedCityArea}
                  radiusKm={radiusKm}
                  onRadiusChange={setRadiusKm}
                  locationCoords={locationCoords}
                  locationStatus={locationStatus}
                  onRequestLocation={handleRequestLiveLocation}
                  selectedSports={selectedSports}
                />
              </div>

              {/* Neighborhood / Area Dropdown */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '16px',
                  marginBottom: '16px',
                }}
              >
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, marginBottom: '6px', color: '#1e293b' }}>
                  Default Sports Hub / Neighborhood
                </label>
                <select
                  value={selectedCityArea}
                  onChange={(e) => setSelectedCityArea(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    fontSize: '13.5px',
                    color: '#0f172a',
                    fontWeight: 600,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="Shahdara, Delhi">Shahdara, Delhi</option>
                  <option value="Connaught Place, New Delhi">Connaught Place, New Delhi</option>
                  <option value="South Delhi, Delhi">South Delhi, Delhi</option>
                  <option value="Bandra West, Mumbai">Bandra West, Mumbai</option>
                  <option value="Andheri, Mumbai">Andheri, Mumbai</option>
                  <option value="Koramangala, Bengaluru">Koramangala, Bengaluru</option>
                  <option value="Indiranagar, Bengaluru">Indiranagar, Bengaluru</option>
                  <option value="HSR Layout, Bengaluru">HSR Layout, Bengaluru</option>
                  <option value="Gachibowli, Hyderabad">Gachibowli, Hyderabad</option>
                  <option value="Salt Lake, Kolkata">Salt Lake, Kolkata</option>
                  <option value="Anna Nagar, Chennai">Anna Nagar, Chennai</option>
                </select>
              </div>

              {/* Statutory 18+ DPDP Age Verification */}
              {needsAgeGate && (
                <div
                  style={{
                    padding: '18px',
                    borderRadius: '16px',
                    background: '#faf5ff',
                    border: '1.5px solid #d8b4fe',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div
                      style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        background: '#7c3aed',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 800,
                      }}
                    >
                      18+
                    </div>
                    <span style={{ fontWeight: 800, fontSize: '13.5px', color: '#581c87' }}>
                      Statutory Age Verification (DPDP Act, 2023 §9)
                    </span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b21a8', margin: '0 0 10px', lineHeight: 1.45 }}>
                    Under Section 9 of the DPDP Act and KhelDrop Terms, KhelDrop is strictly an adult-only (18+) platform.
                  </p>

                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#4c1d95', marginBottom: '4px' }}>
                    Date of Birth <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={onboardingDob}
                    onChange={(e) => {
                      setOnboardingDob(e.target.value);
                      if (e.target.value) {
                        const a = calculateAge(e.target.value);
                        if (a < 18) {
                          setAgeError('Strict age limit: You must be at least 18 years old to join KhelDrop under DPDP Act, 2023 §9.');
                        } else {
                          setAgeError('');
                        }
                      }
                    }}
                    max={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()).toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: ageError ? '1.5px solid #ef4444' : '1px solid #c084fc',
                      background: '#ffffff',
                      fontSize: '13px',
                    }}
                  />

                  {ageError && (
                    <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>
                      {ageError}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px' }}>
                    <input
                      type="checkbox"
                      id="dynamic-onboarding-age-affirm"
                      checked={ageAffirmed}
                      onChange={(e) => setAgeAffirmed(e.target.checked)}
                      style={{ marginTop: '3px', accentColor: '#7c3aed', cursor: 'pointer' }}
                    />
                    <label htmlFor="dynamic-onboarding-age-affirm" style={{ fontSize: '11.5px', color: '#3b0764', lineHeight: 1.4, cursor: 'pointer', margin: 0 }}>
                      I solemnly affirm that I am <strong>18 years of age or older</strong>, competent to contract under Indian Contract Act, 1872, and agree to the <a href="/terms" target="_blank" style={{ color: '#7c3aed', fontWeight: 600, textDecoration: 'underline' }}>Terms</a>, <a href="/privacy" target="_blank" style={{ color: '#7c3aed', fontWeight: 600, textDecoration: 'underline' }}>Privacy</a>, and <a href="/age-policy" target="_blank" style={{ color: '#7c3aed', fontWeight: 600, textDecoration: 'underline' }}>Age Limitation Policy (18+)</a>.
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* FOOTER ACTION CONTROLS */}
          <div
            style={{
              marginTop: '28px',
              paddingTop: '20px',
              borderTop: '1px solid #f0f0f4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: step > 1 ? 'space-between' : 'flex-end',
            }}
          >
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep((prev) => prev - 1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <ArrowLeft size={16} /> Back
              </button>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={handleSkip}
                disabled={saving}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '999px',
                  padding: '11px 22px',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>

              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={() => setStep((prev) => prev + 1)}
                  disabled={step === 2 && selectedSports.length === 0}
                  style={{
                    background: '#09090b',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '999px',
                    padding: '11px 26px',
                    fontSize: '13.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  Continue <ArrowRight size={15} />
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => handleFinishOnboarding({ startTour: true })}
                    disabled={saving || (needsAgeGate && (!onboardingDob || calculateAge(onboardingDob) < 18 || !ageAffirmed))}
                    style={{
                      background: 'rgba(124, 58, 237, 0.1)',
                      color: '#7c3aed',
                      border: '1.5px solid #7c3aed',
                      borderRadius: '999px',
                      padding: '11px 20px',
                      fontSize: '13px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Sparkles size={15} /> Issue Pass & Take Tour
                  </button>

                  <button
                    type="button"
                    onClick={() => handleFinishOnboarding({ startTour: false })}
                    disabled={saving || (needsAgeGate && (!onboardingDob || calculateAge(onboardingDob) < 18 || !ageAffirmed))}
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '999px',
                      padding: '11px 24px',
                      fontSize: '13.5px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 16px rgba(124, 58, 237, 0.4)',
                    }}
                  >
                    <CheckCircle2 size={16} />
                    {saving ? 'Issuing Pass...' : 'Ready to Play'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
